import React from "react";
import SEO from "../components/SEO";
import SoftwareSolutions from "../components/KDSSolutions";
import ContactCTA from "../components/ContactCTA";
import SoftwareDevice from "../assets/images/kdsSection.png";
import MonitorIcon from "../assets/svg/monitor.svg";
import MeterIcon from "../assets/svg/meter.svg";
import VernIcon from "../assets/svg/vern.svg";
import ThumbsIcon from "../assets/svg/thumbsUp.svg";
import FAQ from "../components/FAQ";
import SpanaIcon from "../assets/svg/spana.svg";
import UiIcon from "../assets/svg/ui.svg";
import CloudIcon from "../assets/svg/cloud.svg";
import MultiIcon from "../assets/svg/multi-dev.svg";
function SoftwarePage() {
  const faqs = [
    {
      question: "What is VernPOS Kitchen Display System (KDS)?",
      answer:
        "VernPOS KDS is a digital kitchen screen that replaces paper tickets by displaying real-time orders directly to kitchen staff. It helps streamline food preparation, reduce mistakes, and improve service speed across all kitchen stations.",
    },
    {
      question: "How does KDS integrate with VernPOS?",
      answer:
        "The KDS receives orders instantly from the POS and updates automatically as items progress. This ensures the kitchen always works with the latest information, while reducing communication gaps between front-of-house and back-of-house teams.",
    },
    {
      question: "What makes VernPOS KDS different from others?",
      answer:
        "VernPOS KDS features a clear, colour-coded order view that highlights preparation status, waiting time, and priorities at a glance. Its intuitive layout helps kitchen staff work faster and reduce delays, especially during peak hours.",
    },
    {
      question: "Is the KDS easy for kitchen staff to use?",
      answer:
        "Definitely. The interface is designed for quick taps and high visibility, enabling staff to mark items as New, Preparing, or Ready with almost no learning curve.",
    },
    {
      question: "What devices and setups does KDS support?",
      answer:
        "VernPOS KDS works on kitchen-friendly devices such as tablets, wall-mounted screens, and industrial touch displays. It supports single or multi-station kitchen setups, making it suitable for cafés, restaurants, and high-volume outlets.",
    },
  ];
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="POS Software Solutions | VernPOS Malaysia"
        description="Discover our comprehensive POS software solutions. Cloud-based, scalable, and designed for retail, F&B, and service businesses across Malaysia."
        keywords="POS software Malaysia, cloud POS system, retail software, F&B software, restaurant POS, inventory management, VernPOS"
      />
      {/* Hero Section */}
      <section className="relative min-h-screen overflow-hidden">
        {/* Two-tone Background */}
        <div className="absolute inset-0">
          {/* Red Background - Top 70% */}
          <div className="absolute top-0 left-0 right-0 h-[70%] bg-gradient-to-br bg-vern to-red-700"></div>
          {/* White Background - Bottom 30% */}
          <div className="absolute bottom-0 left-0 right-0 h-[30%] bg-white"></div>
          {/* Smooth Curved Transition */}
          <div className="absolute left-0 right-0" style={{ top: "100%", height: "10%" }}>
            <svg viewBox="0 0 1200 120" className="w-full h-full" preserveAspectRatio="none">
              <path d="M0,0 C400,80 800,80 1200,0 L1200,120 L0,120 Z" fill="white" />
            </svg>
          </div>
        </div>
        {/* Content Container */}
        <div className="relative z-10 container mx-auto max-w-6xl px-4 pt-32 pb-16">
          {/* Centered Text Content */}
          <div className="text-center mb-36">
            <h1 className="text-5xl md:text-5xl lg:text-5xl font-bold text-white mb-6 leading-tight max-w-4xl mx-auto">
              Turn Chaos into Control with a Smarter Kitchen Display System
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed max-w-3xl mx-auto">
              Eliminate paper tickets, reduce mistakes, and serve meals faster — all with one sleek digital screen that keeps your kitchen running in
              perfect sync.
            </p>
          </div>
          {/* POS Software Image - Centered and Overlapping */}
          <div className="relative flex justify-center">
            <div className="relative z-20 transform hover:scale-105 transition-transform duration-300">
              <img
                src={SoftwareDevice}
                alt="Professional POS Software Interface"
                className="w-full max-w-3xl h-auto object-contain drop-shadow-2xl"
                style={{
                  filter: "drop-shadow(0 20px 40px rgba(0,0,0,0.3)) drop-shadow(0 0 20px rgba(220,38,38,0.2))",
                  marginTop: "-100px", // Overlap with navbar
                }}
              />
            </div>
            {/* Subtle Glow Effect */}
            <div
              className="absolute inset-0 z-10 blur-3xl opacity-30"
              style={{
                background: "radial-gradient(ellipse at center, rgba(220,38,38,0.4) 0%, transparent 70%)",
                transform: "scale(1.2)",
              }}
            ></div>
          </div>
        </div>
        {/* Additional Decorative Elements */}
        <div className="absolute top-1/4 left-10 w-20 h-20 bg-white/10 rounded-full blur-xl"></div>
        <div className="absolute top-1/3 right-16 w-32 h-32 bg-red-400/20 rounded-full blur-2xl"></div>
        <div className="absolute bottom-1/4 left-1/4 w-24 h-24 bg-white/5 rounded-full blur-lg"></div>
      </section>
      {/* Built for Efficiency Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-6xl font-bold text-black mb-4">Keep Every Order Moving Smoothly, From POS to Kitchen</h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              No shouting. No lost dockets. No confusion. Just a kitchen that runs like clockwork.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
            <div className="flex items-start p-6 bg-[#f6f6f7] rounded-3xl">
              <div className="bg-[#FCF4F3] rounded-full w-16 h-16 flex items-center justify-center mr-4 flex-shrink-0 my-6">
                <img src={MonitorIcon} alt="Monitor Icon" className="w-8 h-8 text-vern" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-black mb-1 my-2">Lightning-Fast Order Updates</h3>
                <p className="text-gray-600 font-medium">Orders appear instantly — so your kitchen never waits on the front counter.</p>
              </div>
            </div>
            <div className="flex items-start p-6 bg-[#f6f6f7] rounded-3xl">
              <div className="bg-[#FCF4F3] rounded-full w-16 h-16 flex items-center justify-center mr-4 flex-shrink-0 my-6">
                <img src={MeterIcon} alt="Performance Icon" className="w-8 h-8 text-vern" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-black mb-2">Visual Prep Timers That Drive Speed</h3>
                <p className="text-gray-600 font-medium">Each ticket comes alive with real-time timers and color cues to prevent delays.</p>
              </div>
            </div>
            <div className="flex items-start p-6 bg-[#f6f6f7] rounded-3xl">
              <div className="bg-[#FCF4F3] rounded-full w-16 h-16 flex items-center justify-center mr-4 flex-shrink-0 my-6">
                <img src={VernIcon} alt="VernPOS Icon" className="w-8 h-8 text-vern" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-black mb-2">Tap to Prioritize or Bump</h3>
                <p className="text-gray-600 font-medium">Easily mark items as urgent, bump completed ones, or recall with a tap.</p>
              </div>
            </div>
            <div className="flex items-start p-6 bg-[#f6f6f7] rounded-3xl">
              <div className="bg-[#FCF4F3] rounded-full w-16 h-16 flex items-center justify-center mr-4 flex-shrink-0 my-6">
                <img src={ThumbsIcon} alt="Thumbs Up Icon" className="w-8 h-8 text-vern" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-black mb-2">Multi-Station Routing</h3>
                <p className="text-gray-600 font-medium">Auto-direct grill items to the grill, drinks to the bar — no manual sorting needed.</p>
              </div>
            </div>
          </div>
          <div className="text-center mt-12">
            {/* Google Play Badge */}
            <div className="inline-block">
              <a
                href="https://play.google.com/store/apps/details?id=com.vernpos.posnative"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block transition-transform hover:scale-105"
                aria-label="Download Vern POS on Google Play"
              >
                <img
                  src="https://play.google.com/intl/en_us/badges/images/generic/en_badge_web_generic.png"
                  alt="Get Vern POS on Google Play"
                  className="h-20 w-auto md:h-24"
                />
              </a>
            </div>
            {/* <button className="bg-vern hover:bg-red-700 text-white font-semibold py-3 px-8 rounded-full transition-colors duration-300">
                            Start Free Trial
                        </button> */}
          </div>
        </div>{" "}
      </section>
      {/* Software Solutions */}
      <SoftwareSolutions />
      {/* Designed for Simplicity Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">Less Chaos. More Control. Happier Customers.</h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">Because faster kitchens mean faster service — and more repeat business.</p>
          </div>
          {/* Feature Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* Real-Time Tracking */}
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <img src={SpanaIcon} alt="Quick Setup Icon" className="w-12 h-12 text-vern" />
              </div>
              <h3 className="text-xl font-bold text-black mb-2">Real-Time Tracking</h3>
              <p className="text-gray-600 text-sm">See every order in progress and spot bottlenecks before they happen.</p>
            </div>
            {/* Instant POS Sync */}
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <img src={UiIcon} alt="Quick Setup Icon" className="w-12 h-12 text-vern" />
              </div>
              <h3 className="text-xl font-bold text-black mb-2">Instant POS Sync</h3>
              <p className="text-gray-600 text-sm">Orders flow straight from counter to kitchen — zero lag, zero hassle.</p>
            </div>
            {/* Empower Every Station */}
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <img src={CloudIcon} alt="Quick Setup Icon" className="w-12 h-12 text-vern" />
              </div>
              <h3 className="text-xl font-bold text-black mb-2">Empower Every Station</h3>
              <p className="text-gray-600 text-sm">Keep every prep area synchronized — no missed orders, no crossed tickets.</p>
            </div>
            {/* Actionable Insights */}
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <img src={MultiIcon} alt="Quick Setup Icon" className="w-12 h-12 text-vern" />
              </div>
              <h3 className="text-xl font-bold text-black mb-2">Actionable Insights</h3>
              <p className="text-gray-600 text-sm">Track kitchen efficiency, prep times, and team performance at a glance.</p>
            </div>
          </div>
          {/* Google Play Badge */}
          <div className="text-center">
            <div className="inline-block">
              <a
                href="https://play.google.com/store/apps/details?id=com.vernpos.posnative"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block transition-transform hover:scale-105"
                aria-label="Download Vern POS on Google Play"
              >
                <img
                  src="https://play.google.com/intl/en_us/badges/images/generic/en_badge_web_generic.png"
                  alt="Get Vern POS on Google Play"
                  className="h-20 w-auto md:h-24"
                />
              </a>
            </div>
          </div>
        </div>
      </section>
      {/* FQA  */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <FAQ title="Frequently Asked Questions" faqs={faqs} />
      </div>
      {/* Contact CTA */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <ContactCTA />
      </div>
    </div>
  );
}
export default SoftwarePage;
